import { Link, useNavigate, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuSeparator, DropdownMenuLabel,
  DropdownMenuCheckboxItem,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { LayoutGrid, LogOut, User as UserIcon, CalendarDays, Sparkles, Crown, Zap, CreditCard, Mail, TrendingUp, Bell, Activity, Settings as SettingsIcon, ShieldCheck } from "lucide-react";
import TrialBanner from "@/components/app/TrialBanner";
import VerifyEmailBanner from "@/components/app/VerifyEmailBanner";
import OtterIcon from "@/components/app/OtterIcon";
import { milestoneToastsEnabled, setMilestoneToastsEnabled } from "@/lib/local-prefs";

const PLAN_META = {
  free:    { label: "Free",    Icon: Zap,      cls: "border-foreground/40 text-foreground bg-white" },
  starter: { label: "Starter", Icon: Sparkles, cls: "border-primary text-primary bg-primary/5" },
  pro:     { label: "Pro",     Icon: Sparkles, cls: "border-primary text-primary bg-primary/5" },
  agency:  { label: "Agency",  Icon: Crown,    cls: "border-amber-500 text-amber-700 bg-amber-50" },
};

export default function AppHeader({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const loc = useLocation();

  const initials = (user && user.name ? user.name : (user?.email || "U")).slice(0,2).toUpperCase();
  const onLogout = async () => { await logout(); navigate("/login"); };
  const plan = (user && user.plan) || "free";
  const pm = PLAN_META[plan] || PLAN_META.free;
  const PIcon = pm.Icon;

  const openPortal = async () => {
    try {
      const { data } = await api.post("/billing/portal", { return_url: `${window.location.origin}/app` });
      window.location.href = data.url;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't open billing portal");
    }
  };

  const [digestEnabled, setDigestEnabled] = useState(true);
  useEffect(() => {
    if (!user) return;
    api.get("/me/digest-prefs").then(({ data }) => {
      setDigestEnabled(!!data.enabled);
    }).catch(() => {});
  }, [user]);
  const toggleDigest = async (next) => {
    const prev = digestEnabled;
    setDigestEnabled(next);
    try {
      await api.post("/me/digest-prefs", { enabled: next });
      toast.success(next ? "Weekly digest emails on" : "Weekly digest emails off");
    } catch (e) {
      setDigestEnabled(prev);
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't update preference");
    }
  };

  const [milestoneOn, setMilestoneOn] = useState(true);
  useEffect(() => { setMilestoneOn(milestoneToastsEnabled()); }, [user]);
  const toggleMilestoneToasts = (next) => {
    setMilestoneOn(next);
    setMilestoneToastsEnabled(next);
    toast.success(next ? "Milestone toasts on" : "Milestone toasts off");
  };

  // Week-over-week top mover chip — fetches both projects + their sparklines once.
  const [topMover, setTopMover] = useState(null);
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      try {
        const [pRes, sRes] = await Promise.all([
          api.get("/projects"),
          api.get("/projects/scores-summary?weeks=4"),
        ]);
        if (cancelled) return;
        const nameById = {};
        for (const p of pRes.data || []) nameById[p.id] = { name: p.name, domain: p.domain };
        let best = null;
        for (const row of sRes.data || []) {
          const points = row.sparkline || [];
          if (points.length < 2) continue;
          const last = points[points.length - 1].score;
          const prev = points[points.length - 2].score;
          const delta = last - prev;
          if (delta <= 0) continue;
          if (!best || delta > best.delta) {
            best = {
              project_id: row.project_id,
              delta,
              score: last,
              ...(nameById[row.project_id] || {}),
            };
          }
        }
        setTopMover(best);
      } catch (e) { /* non-critical */ }
    })();
    return () => { cancelled = true; };
  }, [user]);

  return (
    <>
    <header className="sticky top-0 z-30 border-b border-border bg-white">
      <div className="px-4 md:px-8 h-14 flex items-center justify-between gap-4">
        <div className="flex items-center gap-6">
          <Link to="/app" className="flex items-center gap-2" data-testid="app-brand-link">
            <div className="w-6 h-6 bg-foreground grid place-items-center text-background">
              <OtterIcon size={16} />
            </div>
            <span className="font-semibold tracking-tight">ArloSEO</span>
          </Link>
          <nav className="hidden md:flex items-center gap-1 text-sm">
            <Link to="/app" data-testid="app-nav-projects">
              <Button variant={loc.pathname === "/app" ? "secondary" : "ghost"} className="rounded-none h-9">
                <LayoutGrid className="w-4 h-4 mr-2" /> Projects
              </Button>
            </Link>
            <Link to="/app/calendar" data-testid="app-nav-calendar">
              <Button variant={loc.pathname.startsWith("/app/calendar") ? "secondary" : "ghost"} className="rounded-none h-9">
                <CalendarDays className="w-4 h-4 mr-2" /> Calendar
              </Button>
            </Link>
          </nav>
          {topMover && (
            <Link
              to={`/app/projects/${topMover.project_id}`}
              data-testid="header-top-mover"
              className="hidden lg:inline-flex items-center gap-1.5 border border-emerald-600/40 bg-emerald-50 px-2.5 py-1 font-mono-data text-[11px] uppercase tracking-widest text-emerald-700 hover:bg-emerald-100 transition-colors"
            >
              <TrendingUp className="w-3 h-3" strokeWidth={2.5} />
              <span className="truncate max-w-[140px]">{topMover.domain || topMover.name}</span>
              <span className="font-semibold">+{topMover.delta}</span>
              <span className="opacity-60">this wk</span>
            </Link>
          )}
          {children}
        </div>
        <div className="flex items-center gap-2">
          {plan === "free" ? (
            <Link to="/pricing">
              <Button className="rounded-none h-8 px-3 bg-primary text-background hover:bg-primary/90" data-testid="header-upgrade-btn">
                <Sparkles className="w-3.5 h-3.5 mr-1.5" /> Upgrade
              </Button>
            </Link>
          ) : (
            <span className={`hidden md:inline-flex items-center gap-1 border px-2 py-1 font-mono-data text-[10px] uppercase tracking-widest ${pm.cls}`} data-testid="header-plan-badge">
              <PIcon className="w-3 h-3" /> {pm.label}
            </span>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button type="button" data-testid="app-user-menu" className="flex items-center gap-2 border border-border px-2 py-1 hover:border-foreground transition-colors">
                <div className="w-6 h-6 bg-foreground text-background grid place-items-center font-mono-data text-xs">{initials}</div>
                <span className="hidden md:inline text-xs font-mono-data truncate max-w-[160px]">{user?.email}</span>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="rounded-none w-56">
              <DropdownMenuLabel className="font-mono-data text-xs">{user?.email}</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem disabled>
                <UserIcon className="w-4 h-4 mr-2" /> {user?.name || "Account"}
              </DropdownMenuItem>
              <DropdownMenuItem disabled className="opacity-100 cursor-default">
                <PIcon className="w-4 h-4 mr-2" /> Plan: {pm.label}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate("/app/settings")} data-testid="menu-settings">
                <SettingsIcon className="w-4 h-4 mr-2" /> Settings
              </DropdownMenuItem>
              {plan === "free" ? (
                <DropdownMenuItem onClick={() => navigate("/pricing")} data-testid="menu-upgrade">
                  <Sparkles className="w-4 h-4 mr-2" /> Upgrade plan
                </DropdownMenuItem>
              ) : (
                <DropdownMenuItem onClick={openPortal} data-testid="menu-manage-billing">
                  <CreditCard className="w-4 h-4 mr-2" /> Manage billing
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuCheckboxItem
                checked={digestEnabled}
                onCheckedChange={toggleDigest}
                onSelect={(e) => e.preventDefault()}
                data-testid="menu-digest-toggle"
              >
                <Mail className="w-4 h-4 mr-2" /> Weekly digest email
              </DropdownMenuCheckboxItem>
              <DropdownMenuCheckboxItem
                checked={milestoneOn}
                onCheckedChange={toggleMilestoneToasts}
                onSelect={(e) => e.preventDefault()}
                data-testid="menu-milestone-toggle"
              >
                <Bell className="w-4 h-4 mr-2" /> Milestone toasts
              </DropdownMenuCheckboxItem>
              {user?.role === "admin" && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuLabel className="font-mono-data text-[10px] uppercase tracking-widest text-muted-foreground">
                    Operator
                  </DropdownMenuLabel>
                  <DropdownMenuItem onClick={() => navigate("/admin")} data-testid="menu-admin-console">
                    <ShieldCheck className="w-4 h-4 mr-2" /> Admin console
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/admin/metrics")} data-testid="menu-admin-metrics">
                    <Activity className="w-4 h-4 mr-2" /> Metrics
                  </DropdownMenuItem>
                </>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={onLogout} data-testid="app-logout-button">
                <LogOut className="w-4 h-4 mr-2" /> Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
    <TrialBanner />
    <VerifyEmailBanner />
    </>
  );
}
